"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useIsLaunchTheme } from "@/theme/ThemeProvider";

/**
 * The footer wordmark outline. SVG gradient stops and their axis cannot be
 * expressed as CSS custom properties on a <stop>, so this is one of the few
 * places that reads the active theme in JS rather than in CSS.
 */
const WORDMARK_AXIS = {
	launch: { x1: "1.31348", y1: "228.412", x2: "1213.4", y2: "24.4827" },
	default: { x1: "635.313", y1: "30.9683", x2: "635.313", y2: "201.288" },
} as const;

const WordmarkStops = () =>
	useIsLaunchTheme() ? (
		<>
			<stop stopColor="#A85A70" />
			<stop offset="0.5161" stopColor="#DDBCC5" />
			<stop offset="1" stopColor="#F3ECEC" />
		</>
	) : (
		<>
			<stop stopColor="#6A6A6A" />
			<stop offset="1" stopColor="#6A6A6A" stopOpacity="0" />
		</>
	);

const Footer = () => {
	const pathname = usePathname();
	const wordmarkAxis = useIsLaunchTheme()
		? WORDMARK_AXIS.launch
		: WORDMARK_AXIS.default;

	// Match Header/BottomNav: fully unmount on onboarding so policy Links
	// don't prefetch and hit middleware redirects before the cookie is set.
	// Also unmount on the standalone /game overlay.
	if (pathname?.startsWith("/onboarding") || pathname?.startsWith("/game")) {
		return null;
	}

	return (
		<footer className={"am-footer-outer"}>
			<div
				className={
					"am-footer-ground relative overflow-hidden min-h-[400px] rounded-t-[48px] md:rounded-t-[96px]"
				}
			>
				{/* Drawn only by themes that define them — see components.css */}
				<div className="am-footer-edge pointer-events-none absolute inset-x-0 top-0 -bottom-1 rounded-t-[48px] md:rounded-t-[96px]" />
				<div className="am-footer-bloom pointer-events-none absolute inset-x-0 top-0 h-[260px]" />

				{/* Background Text */}
				<div className="absolute bottom-0 left-0 w-full flex items-end justify-center">
					<svg
						width="1215"
						height="237"
						viewBox="0 0 1215 237"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						className="launch-wordmark"
					>
						<mask
							id="path-1-outside-1_2453_3356"
							maskUnits="userSpaceOnUse"
							x="-0.686523"
							y="0"
							width="1216"
							height="237"
							fill="black"
						>
							<rect fill="white" x="-0.686523" width="1216" height="237" />
							<path d="M119.161 24.4827L171.434 228.412H119.161L107.407 178.047H50.1844L38.74 228.412H1.31348L57.6079 24.4827H119.161ZM56.0613 153.638H101.839L78.6409 54.7631L56.0613 153.638Z" />
							<path d="M328.525 197.513C328.525 203.281 329.246 208.328 330.69 212.653C332.133 216.979 334.608 220.79 338.113 224.086C335.639 226.97 332.133 229.133 327.597 230.574C323.266 232.222 318.833 233.046 314.296 233.046C306.873 233.046 300.584 231.501 295.428 228.412C290.273 225.116 286.768 220.481 284.912 214.507C280.788 221.511 275.014 226.661 267.591 229.956C260.373 233.458 252.228 235.209 243.155 235.209C228.102 235.209 216.554 232.016 208.512 225.631C200.677 219.245 195.418 210.388 192.738 199.058C190.057 187.729 188.717 173.207 188.717 155.492V70.8302H236.66V153.947C236.66 166.924 236.969 177.429 237.588 185.463C238.412 193.29 240.371 199.882 243.464 205.238C246.764 210.388 251.816 212.962 258.621 212.962C266.456 212.962 272.127 209.667 275.633 203.075C279.344 196.483 281.2 188.244 281.2 178.356V70.8302H328.525V197.513Z" />
							<path d="M349.05 84.7344V75.156C358.948 71.4482 368.949 66.2984 379.053 59.7068C389.157 53.1152 397.509 46.1115 404.107 38.6959H417.408V70.8302H445.864V84.7344H417.098V194.732C417.098 201.118 418.233 206.062 420.501 209.564C422.975 212.859 426.893 215.022 432.255 216.052C430.193 221.408 426.378 225.528 420.81 228.412C415.243 231.501 409.056 233.046 402.252 233.046C391.529 233.046 383.384 230.162 377.816 224.395C372.455 218.627 369.774 210.285 369.774 199.367V84.7344H349.05Z" />
							<path d="M527.691 64.6505C552.436 64.6505 570.995 71.9631 583.367 86.5883C595.946 101.214 602.235 121.503 602.235 147.458C602.235 174.237 595.946 195.556 583.367 211.417C570.788 227.279 552.23 235.209 527.691 235.209C503.359 235.209 484.8 227.588 472.015 212.344C459.231 197.101 452.838 176.502 452.838 150.548C452.838 124.181 459.128 103.273 471.706 87.8243C484.491 72.3751 503.153 64.6505 527.691 64.6505ZM527.691 216.67C538.414 216.67 545.322 210.388 548.415 197.822C551.508 185.257 553.055 168.984 553.055 149.003C553.055 130.052 551.405 114.397 548.106 102.037C544.806 89.4722 538.002 83.1895 527.691 83.1895C517.381 83.1895 510.576 89.4722 507.277 102.037C503.977 114.603 502.328 130.258 502.328 149.003C502.328 168.984 503.874 185.257 506.967 197.822C510.061 210.388 516.968 216.67 527.691 216.67Z" />
							<path d="M841.322 24.4827V228.412H789.976V64.3415L740.177 228.412H710.484L660.685 64.0326V228.412H628.517V24.4827H698.421L735.847 154.565L772.964 24.4827H841.322Z" />
							<path d="M900.354 1C908.808 1 915.51 2.9569 920.459 6.8707C925.614 10.7845 928.192 16.2432 928.192 23.2468C928.192 30.4564 925.511 36.0181 920.15 39.9319C914.789 43.6397 907.881 45.4936 899.426 45.4936C891.178 45.4936 884.373 43.6397 879.012 39.9319C873.856 36.0181 871.279 30.6624 871.279 23.8647C871.279 16.6551 873.96 11.0935 879.321 7.17967C884.888 3.05989 891.9 1 900.354 1ZM922.624 197.513C922.624 203.281 923.346 208.328 924.789 212.653C926.233 216.979 928.707 220.79 932.213 224.086C929.945 226.97 926.439 229.133 921.696 230.574C917.16 232.222 912.623 233.046 908.087 233.046C897.364 233.046 889.219 230.368 883.651 225.013C878.084 219.451 875.3 211.314 875.3 200.603V70.8302H922.624V197.513Z" />
							<path d="M958.978 70.8302L997.642 67.1224L999.807 94.9309C1007.64 74.744 1023.83 64.6505 1048.37 64.6505C1066.31 64.6505 1078.99 69.4913 1086.41 79.1727C1094.04 88.8542 1097.86 103.376 1097.86 122.739V197.513C1097.86 203.281 1098.58 208.328 1100.02 212.653C1101.47 216.979 1103.94 220.79 1107.45 224.086C1105.18 226.97 1101.67 229.133 1096.93 230.574C1092.39 232.222 1087.86 233.046 1083.32 233.046C1072.6 233.046 1064.45 230.368 1058.89 225.013C1053.32 219.451 1050.53 211.314 1050.53 200.603V125.211C1050.53 113.058 1049.4 103.891 1047.13 97.7117C1044.86 91.3261 1039.5 88.1332 1031.05 88.1332C1020.94 88.1332 1014.24 92.768 1010.94 102.037C1007.85 111.307 1006.3 123.563 1006.3 138.806V228.412H958.978V70.8302Z" />
							<path d="M1116.59 84.7344V75.156C1126.48 71.4482 1136.48 66.2984 1146.59 59.7068C1156.69 53.1152 1165.04 46.1115 1171.64 38.6959H1184.94V70.8302H1213.4V84.7344H1184.63V194.732C1184.63 201.118 1185.77 206.062 1188.04 209.564C1190.51 212.859 1194.43 215.022 1199.79 216.052C1197.73 221.408 1193.91 225.528 1188.35 228.412C1182.78 231.501 1176.59 233.046 1169.79 233.046C1159.06 233.046 1150.92 230.162 1145.35 224.395C1139.99 218.627 1137.31 210.285 1137.31 199.367V84.7344H1116.59Z" />
						</mask>
						<path
							d="M119.161 24.4827L171.434 228.412H119.161L107.407 178.047H50.1844L38.74 228.412H1.31348L57.6079 24.4827H119.161ZM56.0613 153.638H101.839L78.6409 54.7631L56.0613 153.638Z"
							stroke="url(#paint0_linear_2453_3356)"
							strokeWidth="2"
							mask="url(#path-1-outside-1_2453_3356)"
						/>
						<path
							d="M328.525 197.513C328.525 203.281 329.246 208.328 330.69 212.653C332.133 216.979 334.608 220.79 338.113 224.086C335.639 226.97 332.133 229.133 327.597 230.574C323.266 232.222 318.833 233.046 314.296 233.046C306.873 233.046 300.584 231.501 295.428 228.412C290.273 225.116 286.768 220.481 284.912 214.507C280.788 221.511 275.014 226.661 267.591 229.956C260.373 233.458 252.228 235.209 243.155 235.209C228.102 235.209 216.554 232.016 208.512 225.631C200.677 219.245 195.418 210.388 192.738 199.058C190.057 187.729 188.717 173.207 188.717 155.492V70.8302H236.66V153.947C236.66 166.924 236.969 177.429 237.588 185.463C238.412 193.29 240.371 199.882 243.464 205.238C246.764 210.388 251.816 212.962 258.621 212.962C266.456 212.962 272.127 209.667 275.633 203.075C279.344 196.483 281.2 188.244 281.2 178.356V70.8302H328.525V197.513Z"
							stroke="url(#paint1_linear_2453_3356)"
							strokeWidth="2"
							mask="url(#path-1-outside-1_2453_3356)"
						/>
						<path
							d="M349.05 84.7344V75.156C358.948 71.4482 368.949 66.2984 379.053 59.7068C389.157 53.1152 397.509 46.1115 404.107 38.6959H417.408V70.8302H445.864V84.7344H417.098V194.732C417.098 201.118 418.233 206.062 420.501 209.564C422.975 212.859 426.893 215.022 432.255 216.052C430.193 221.408 426.378 225.528 420.81 228.412C415.243 231.501 409.056 233.046 402.252 233.046C391.529 233.046 383.384 230.162 377.816 224.395C372.455 218.627 369.774 210.285 369.774 199.367V84.7344H349.05Z"
							stroke="url(#paint2_linear_2453_3356)"
							strokeWidth="2"
							mask="url(#path-1-outside-1_2453_3356)"
						/>
						<path
							d="M527.691 64.6505C552.436 64.6505 570.995 71.9631 583.367 86.5883C595.946 101.214 602.235 121.503 602.235 147.458C602.235 174.237 595.946 195.556 583.367 211.417C570.788 227.279 552.23 235.209 527.691 235.209C503.359 235.209 484.8 227.588 472.015 212.344C459.231 197.101 452.838 176.502 452.838 150.548C452.838 124.181 459.128 103.273 471.706 87.8243C484.491 72.3751 503.153 64.6505 527.691 64.6505ZM527.691 216.67C538.414 216.67 545.322 210.388 548.415 197.822C551.508 185.257 553.055 168.984 553.055 149.003C553.055 130.052 551.405 114.397 548.106 102.037C544.806 89.4722 538.002 83.1895 527.691 83.1895C517.381 83.1895 510.576 89.4722 507.277 102.037C503.977 114.603 502.328 130.258 502.328 149.003C502.328 168.984 503.874 185.257 506.967 197.822C510.061 210.388 516.968 216.67 527.691 216.67Z"
							stroke="url(#paint3_linear_2453_3356)"
							strokeWidth="2"
							mask="url(#path-1-outside-1_2453_3356)"
						/>
						<path
							d="M841.322 24.4827V228.412H789.976V64.3415L740.177 228.412H710.484L660.685 64.0326V228.412H628.517V24.4827H698.421L735.847 154.565L772.964 24.4827H841.322Z"
							stroke="url(#paint4_linear_2453_3356)"
							strokeWidth="2"
							mask="url(#path-1-outside-1_2453_3356)"
						/>
						<path
							d="M900.354 1C908.808 1 915.51 2.9569 920.459 6.8707C925.614 10.7845 928.192 16.2432 928.192 23.2468C928.192 30.4564 925.511 36.0181 920.15 39.9319C914.789 43.6397 907.881 45.4936 899.426 45.4936C891.178 45.4936 884.373 43.6397 879.012 39.9319C873.856 36.0181 871.279 30.6624 871.279 23.8647C871.279 16.6551 873.96 11.0935 879.321 7.17967C884.888 3.05989 891.9 1 900.354 1ZM922.624 197.513C922.624 203.281 923.346 208.328 924.789 212.653C926.233 216.979 928.707 220.79 932.213 224.086C929.945 226.97 926.439 229.133 921.696 230.574C917.16 232.222 912.623 233.046 908.087 233.046C897.364 233.046 889.219 230.368 883.651 225.013C878.084 219.451 875.3 211.314 875.3 200.603V70.8302H922.624V197.513Z"
							stroke="url(#paint5_linear_2453_3356)"
							strokeWidth="2"
							mask="url(#path-1-outside-1_2453_3356)"
						/>
						<path
							d="M958.978 70.8302L997.642 67.1224L999.807 94.9309C1007.64 74.744 1023.83 64.6505 1048.37 64.6505C1066.31 64.6505 1078.99 69.4913 1086.41 79.1727C1094.04 88.8542 1097.86 103.376 1097.86 122.739V197.513C1097.86 203.281 1098.58 208.328 1100.02 212.653C1101.47 216.979 1103.94 220.79 1107.45 224.086C1105.18 226.97 1101.67 229.133 1096.93 230.574C1092.39 232.222 1087.86 233.046 1083.32 233.046C1072.6 233.046 1064.45 230.368 1058.89 225.013C1053.32 219.451 1050.53 211.314 1050.53 200.603V125.211C1050.53 113.058 1049.4 103.891 1047.13 97.7117C1044.86 91.3261 1039.5 88.1332 1031.05 88.1332C1020.94 88.1332 1014.24 92.768 1010.94 102.037C1007.85 111.307 1006.3 123.563 1006.3 138.806V228.412H958.978V70.8302Z"
							stroke="url(#paint6_linear_2453_3356)"
							strokeWidth="2"
							mask="url(#path-1-outside-1_2453_3356)"
						/>
						<path
							d="M1116.59 84.7344V75.156C1126.48 71.4482 1136.48 66.2984 1146.59 59.7068C1156.69 53.1152 1165.04 46.1115 1171.64 38.6959H1184.94V70.8302H1213.4V84.7344H1184.63V194.732C1184.63 201.118 1185.77 206.062 1188.04 209.564C1190.51 212.859 1194.43 215.022 1199.79 216.052C1197.73 221.408 1193.91 225.528 1188.35 228.412C1182.78 231.501 1176.59 233.046 1169.79 233.046C1159.06 233.046 1150.92 230.162 1145.35 224.395C1139.99 218.627 1137.31 210.285 1137.31 199.367V84.7344H1116.59Z"
							stroke="url(#paint7_linear_2453_3356)"
							strokeWidth="2"
							mask="url(#path-1-outside-1_2453_3356)"
						/>
						<defs>
							<linearGradient
								id="paint0_linear_2453_3356"
								{...wordmarkAxis}
								gradientUnits="userSpaceOnUse"
							>
								<WordmarkStops />
							</linearGradient>
							<linearGradient
								id="paint1_linear_2453_3356"
								{...wordmarkAxis}
								gradientUnits="userSpaceOnUse"
							>
								<WordmarkStops />
							</linearGradient>
							<linearGradient
								id="paint2_linear_2453_3356"
								{...wordmarkAxis}
								gradientUnits="userSpaceOnUse"
							>
								<WordmarkStops />
							</linearGradient>
							<linearGradient
								id="paint3_linear_2453_3356"
								{...wordmarkAxis}
								gradientUnits="userSpaceOnUse"
							>
								<WordmarkStops />
							</linearGradient>
							<linearGradient
								id="paint4_linear_2453_3356"
								{...wordmarkAxis}
								gradientUnits="userSpaceOnUse"
							>
								<WordmarkStops />
							</linearGradient>
							<linearGradient
								id="paint5_linear_2453_3356"
								{...wordmarkAxis}
								gradientUnits="userSpaceOnUse"
							>
								<WordmarkStops />
							</linearGradient>
							<linearGradient
								id="paint6_linear_2453_3356"
								{...wordmarkAxis}
								gradientUnits="userSpaceOnUse"
							>
								<WordmarkStops />
							</linearGradient>
							<linearGradient
								id="paint7_linear_2453_3356"
								{...wordmarkAxis}
								gradientUnits="userSpaceOnUse"
							>
								<WordmarkStops />
							</linearGradient>
						</defs>
					</svg>
				</div>

				{/* Content */}
				{/* LAUNCH THEME (temporary): z-10 is dropped while the campaign runs
				    — it would open a stacking context and isolate the certification
				    badges' mix-blend from the footer background. DOM order already
				    paints this above the wordmark either way. */}
				<div
					className={
						"am-footer-content relative mt-10 flex flex-col gap-6 px-9 md:px-12 pb-[200px] md:pb-0"
					}
				>
					{/* Top row: Policy Links + Badges & Safety */}
					<div className="flex flex-col gap-8 md:flex-row md:justify-between md:items-start md:gap-12">
						{/* Policy Links - 2 columns on mobile, inline row on desktop */}
						<nav className="grid grid-cols-2 gap-x-12 gap-y-4 text-white text-sm md:flex md:flex-row md:gap-x-8 md:gap-y-0 md:items-start">
							<Link href="/terms-of-use" className="underline">
								Terms of Use
							</Link>
							<Link href="/cancellation-and-refund" className="underline">
								Cancellation & Refund
							</Link>
							<Link href="/shipping-policy" className="underline">
								Shipping Policy
							</Link>
							<Link href="/privacy-policy" className="underline">
								Privacy Policy
							</Link>
							<Link href="/dpa" className="underline">
								DPA
							</Link>
						</nav>

						{/* Badges + Safety Text. The certification marks carry a baked
						    black backing rather than transparency — screen drops it into
						    the wine ground and leaves the white artwork alone. */}
						<div className="flex items-center gap-3">
							<div className="flex items-center gap-2 flex-shrink-0">
								<Image
									src="/new_icons/footer/ios.svg"
									alt="ISO"
									width={64}
									height={64}
									className={"am-cert-badge w-[46px] h-[46px] md:w-16 md:h-16"}
								/>
								<Image
									src="/new_icons/footer/iec.svg"
									alt="IEC"
									width={64}
									height={64}
									className={"am-cert-badge w-[46px] h-[46px] md:w-16 md:h-16"}
								/>
								<Image
									src="/new_icons/footer/soc.svg"
									alt="SOC 2 Type 1"
									width={64}
									height={64}
									className={"am-cert-badge w-[46px] h-[46px] md:w-16 md:h-16"}
								/>
							</div>
							<div>
								<p className="text-white text-sm md:text-[22px] font-medium leading-tight">
									Your data is safe with us
								</p>
								<p className="text-footer-text-gray text-sm md:text-[15px] font-light leading-snug mt-1">
									We are certified for ISO IEC 27001:2022,
									<br />
									ISO IEC 27701:2019 and SOC 2 Type 1
								</p>
							</div>
						</div>
					</div>

					{/* Copyright */}
					<p className="font-light text-[13px] text-footer-text-gray leading-[120%]">
						&copy; {new Date().getFullYear()} AutoMint. All rights reserved.
					</p>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
